import React, { useMemo, useState, useCallback, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { Trans } from 'react-i18next';
import { useField } from 'react-final-form';
import useCheckbox from '@ubeya/shared-web/hooks/Table/useCheckbox';
import {
  Table as BaseTable,
  Filter,
  Small,
  FlexSpace,
  FlexColumn,
  SmallBody,
  CheckBox,
  Search
} from '@ubeya/shared-web/components';
import { applyFilter, applyFilters } from '@ubeya/shared-web/utils/filters';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useLocations from '@ubeya/shared/hooks/account/useLocations';
import { FILTERS_IDS } from '@ubeya/shared/constants';
import EmployeesModal from './EmployeesModal';
import EarningsLimitCheckBox, { applyEarningsLimitFilter, hasEarningsLimitFilter } from './EarningsLimitCheckBox';

const Link = styled(Small)`
  cursor: pointer;
  text-decoration: underline;
  margin-right: 10px;
`;

const Title = styled(SmallBody)``;

const Duration = styled.span`
  unicode-bidi: plaintext;
`;

const TableWrapper = styled(FlexColumn)`
  flex: 1;
`;

const PublishCheckboxContainer = styled(FlexColumn)`
  margin: 10px 5px 5px;
`;

const PubishToPreferred = styled(FlexColumn)`
  margin: 5px 5px;
`;

const Table = styled(BaseTable)`
  .cell {
    border-right: none !important;
  }
  .cell:first-child {
    padding: 5px !important;
  }

  .cell.count {
    justify-content: flex-end;
  }
`;

// const FiltersRow = styled(BaseFiltersRow)`
//   margin-top: 0;
// `;

// const Counter = styled(FlexCenter)`
//   ${extraSmall};
//   background: ${({ theme }) => theme.colors.grey2};
//   border-radius: 50%;
//   width: 20px;
//   height: 20px;
// `;

const CountCell = ({
  isShift,
  isOpen,
  setIsOpen,
  filters,
  setFilters,
  checked,
  setChecked,
  projectDate,
  clientId,
  disabledFilters,
  branches,
  shiftId,
  shiftDate,
  locationId,
  params: externalParams
}) => {
  const params = useMemo(
    () => ({
      activeTab: 'active',
      search: '',
      filters,
      projectDate,
      clientId,
      branches,
      enabled: !!isOpen,
      ...externalParams
    }),
    [filters, projectDate, clientId, branches, isOpen, externalParams]
  );
  // const { totalItems } = useEmployees(params);
  const checkboxData = useCheckbox({ defaultChecked: checked });

  // const totalSelected = useMemo(() => totalCheckboxSelected({ value: checked, total: totalItems }), [
  //   checked,
  //   totalItems
  // ]);

  // const counter = useMemo(
  //   () =>
  //     Object.values(filters).reduce(
  //       (sum, array) => sum + array.reduce((prevSum, { value }) => prevSum + (counterFormatter(value) ? 1 : 0), 0),
  //       0
  //     ),
  //   [filters]
  // );

  const selectedItem = { shiftId, shiftDate, locationId };

  return (
    <FlexSpace>
      <Link onClick={() => setIsOpen(true)}>
        {/* {totalSelected || 0}  */}
        <Trans>view</Trans>
      </Link>

      {/* <DropDown
        renderValue={<Counter>{counter}</Counter>}
        menuOffset={{ top: 10, right: 20 }}
        usePortal
        showArrow={false}>
        <FiltersRow
          selectedKey="employees"
          filters={filters}
          setFilters={setFilters}
          usePortal
          filterContextParams={params}
          disabledFilters={disabledFilters}
        />
      </DropDown> */}

      {isOpen && (
        <EmployeesModal
          isShift={isShift}
          params={params}
          checkboxData={checkboxData}
          filters={filters}
          setFilters={setFilters}
          item={selectedItem}
          branches={branches}
          disabledFilters={disabledFilters}
          onClose={() => setIsOpen(false)}
          onConfirm={() => {
            setChecked(checkboxData.value);
            setIsOpen(false);
          }}
        />
      )}
    </FlexSpace>
  );
};

const PublishEmployees = ({
  isShift,
  items,
  value,
  search,
  setSearch,
  onChange,
  generateDisabledFilters,
  branches,
  params = {}
}) => {
  const {
    input: { value: checkedValue, onChange: onCheckedChange }
  } = useField('checkedValue');

  const { t, isEmployeePreferredAreasEnabled } = useContext(ConfigContext);
  const { mappedLocations } = useLocations();

  const isAreaShift = useCallback(({ locationId }) => !!mappedLocations?.[locationId]?.isSubLocation, [
    mappedLocations
  ]);
  const {
    config: { hasTimeAvailability }
  } = useAccount();

  const [openModal, setOpenModal] = useState({});
  const checkboxData = useCheckbox({ defaultChecked: checkedValue });

  const { projectDate, clientId } = params || {};

  const filteredItems = useMemo(() => {
    if (!search) return items;

    const searchLower = search.toLowerCase();
    return items.filter(({ title, duration, comments }) => {
      const searchableText = [title, duration, comments].filter(Boolean).join(' ').toLowerCase();

      return searchableText.includes(searchLower);
    });
  }, [items, search]);

  useEffect(() => {
    onCheckedChange(checkboxData.value);
  }, [checkboxData.value, onCheckedChange]);

  const handleChangeValue = useCallback(
    (id, payload) => onChange({ ...value, [id]: { ...(value[id] || {}), ...payload } }),
    [onChange, value]
  );

  const handleChangeAllFilters = useCallback(
    (values) =>
      onChange(
        Object.entries(value).reduce((prev, [key, item]) => {
          const valuesFormatted = {
            basic: (values?.basic || []).reduce((acc, basicFilter) => ({ ...acc, [basicFilter.id]: basicFilter }), {}),
            fields: (values?.fields || []).reduce((acc, fieldFilter) => ({ ...acc, [fieldFilter.id]: fieldFilter }), {})
          };
          const updatedFilters = applyFilters({ values: valuesFormatted, allFilters: item.filters || {} });

          return {
            ...prev,
            [key]: {
              ...item,
              filters: updatedFilters
            }
          };
        }, {})
      ),
    [onChange, value]
  );

  const handleChangePublishToNonBooked = useCallback(
    (newValue) => {
      onChange(
        Object.entries(value).reduce((prev, [key, item]) => {
          return {
            ...prev,
            [key]: {
              ...item,
              filters: applyFilter({
                type: 'basic',
                id: FILTERS_IDS.NOT_BOOKED_ON_DATE_FILTER_ID,
                value: newValue ? item.shiftDate || projectDate : null,
                allFilters: item.filters || {}
              })
            }
          };
        }, {})
      );
    },
    [onChange, projectDate, value]
  );

  const handleChangeEarningsLimit = useCallback(
    (checked) =>
      onChange(
        Object.entries(value).reduce(
          (prev, [key, item]) => ({
            ...prev,
            [key]: {
              ...item,
              filters: applyEarningsLimitFilter({ filters: item.filters, shiftId: item.shiftId, checked })
            }
          }),
          {}
        )
      ),
    [onChange, value]
  );

  const handleChangePublishToPreferredClient = useCallback(
    (newValue) => {
      handleChangeAllFilters({
        basic: [{ id: FILTERS_IDS.PREFERRED_CLIENTS_FILTER_ID, value: newValue ? [clientId] : null }]
      });
    },
    [handleChangeAllFilters, clientId]
  );

  const handleChangeAvailableByTimeAvailability = useCallback(
    (newValue) =>
      onChange(
        Object.entries(value).reduce((prev, [key, item]) => {
          return {
            ...prev,
            [key]: {
              ...item,
              filters: applyFilter({
                type: 'basic',
                id: FILTERS_IDS.AVAILABLE_BY_TIME_AVAILABILITY,
                value: newValue ? item.shiftId : null,
                allFilters: item.filters || {}
              })
            }
          };
        }, {})
      ),
    [onChange, value]
  );

  const handleChangeWorkedInAreaBefore = useCallback(
    (newValue) => {
      onChange(
        Object.entries(value).reduce((prev, [key, item]) => {
          return {
            ...prev,
            [key]: {
              ...item,
              filters: applyFilter({
                type: 'basic',
                id: FILTERS_IDS.WORKED_IN_AREA_BEFORE,
                value: newValue && item.locationId ? [item.locationId] : null,
                allFilters: item.filters || {}
              })
            }
          };
        }, {})
      );
    },
    [onChange, value]
  );

  const handleChangePreferredArea = useCallback(
    (newValue) => {
      onChange(
        Object.entries(value).reduce((prev, [key, item]) => {
          return {
            ...prev,
            [key]: {
              ...item,
              filters: applyFilter({
                type: 'basic',
                id: FILTERS_IDS.PREFERRED_AREA,
                // Only shifts on an area can match a preferred area; writing the filter for a shift
                // on a top-level location would publish it to nobody.
                value: newValue && isAreaShift(item) ? [item.locationId] : null,
                allFilters: item.filters || {}
              })
            }
          };
        }, {})
      );
    },
    [onChange, value, isAreaShift]
  );

  const crossShiftsDisabledFilters = useMemo(
    () =>
      new Array(
        ...new Set(
          filteredItems.reduce((all, { positionId }) => {
            all.push(...(generateDisabledFilters ? generateDisabledFilters(positionId) : []));
            return all;
          }, []) || []
        )
      ),
    [generateDisabledFilters, filteredItems]
  );

  // extracted to useCallback to avoid re-rendering and re-fetching the data from useEmployees on search change
  const countCell = useCallback(
    ({ rowData: { id, positionId, shiftDate, locationId } }) => (
      <CountCell
        isShift={isShift}
        isOpen={openModal[id]}
        setIsOpen={(openValue) => setOpenModal({ ...openModal, [id]: openValue })}
        filters={value[id]?.filters || {}}
        setFilters={(values) => handleChangeValue(id, { filters: values })}
        checked={value[id]?.audience}
        setChecked={(isCheckedValue) => handleChangeValue(id, { audience: isCheckedValue })}
        disabledFilters={generateDisabledFilters ? generateDisabledFilters(positionId) : []}
        branches={branches}
        clientId={clientId}
        shiftDate={shiftDate}
        projectDate={projectDate}
        shiftId={id}
        params={params}
        locationId={locationId}
      />
    ),
    [branches, clientId, generateDisabledFilters, handleChangeValue, isShift, openModal, params, projectDate, value]
  );

  const columns = useMemo(
    () => [
      {
        Header: '',
        dataKey: 'title',
        Cell: ({ rowData: { title, duration, comments } }) => (
          <Title>
            {`${title}, `}
            <Duration>{duration}</Duration>
            {comments && `, ${comments}`}
          </Title>
        )
      },

      {
        Header: () => (
          <>
            <Search value={search} onChange={setSearch} keepOpen />
            <Filter
              selectedKey="employees"
              disabledFilters={crossShiftsDisabledFilters}
              setFilters={handleChangeAllFilters}
              usePortal
              filterContextParams={params}
            />
          </>
        ),
        dataKey: 'count',
        Cell: countCell,
        maxWidth: 190,
        className: 'count'
      }
    ],
    [countCell, search, setSearch, crossShiftsDisabledFilters, handleChangeAllFilters, params]
  );

  const publishToNonBooked = useMemo(
    () =>
      Object.values(value).every(({ filters }) =>
        (filters?.basic || []).find(({ id }) => id === FILTERS_IDS.NOT_BOOKED_ON_DATE_FILTER_ID)
      ),
    [value]
  );

  const earningsLimit = useMemo(() => Object.values(value).every(({ filters }) => hasEarningsLimitFilter(filters)), [
    value
  ]);

  const publishToPreferredClient = useMemo(
    () =>
      Object.values(value).every(({ filters }) =>
        (filters?.basic || []).find(({ id }) => id === FILTERS_IDS.PREFERRED_CLIENTS_FILTER_ID)
      ),
    [value]
  );

  const availableByTimeAvailability = useMemo(
    () =>
      Object.values(value).every(({ filters }) =>
        (filters?.basic || []).find(({ id }) => id === FILTERS_IDS.AVAILABLE_BY_TIME_AVAILABILITY)
      ),
    [value]
  );

  const publishToWorkedInAreaBefore = useMemo(
    () =>
      Object.values(value).every(({ filters, locationId }) => {
        const areaFilter = (filters?.basic || []).find(({ id }) => id === FILTERS_IDS.WORKED_IN_AREA_BEFORE);
        return locationId && Array.isArray(areaFilter?.value) && areaFilter?.value.includes(locationId);
      }),
    [value]
  );

  const areaShifts = useMemo(() => Object.values(value).filter(isAreaShift), [value, isAreaShift]);

  const publishToPreferredArea = useMemo(
    () =>
      areaShifts.length > 0 &&
      areaShifts.every(({ filters, locationId }) => {
        const preferredAreaFilter = (filters?.basic || []).find(({ id }) => id === FILTERS_IDS.PREFERRED_AREA);
        return Array.isArray(preferredAreaFilter?.value) && preferredAreaFilter?.value.includes(locationId);
      }),
    [areaShifts]
  );

  const showWorkedInAreaBefore = projectDate;
  // Preferred areas are always sub-locations, so shifts on top-level locations can never match
  const showPreferredArea = useMemo(() => !!projectDate && !!isEmployeePreferredAreasEnabled && areaShifts.length > 0, [
    projectDate,
    isEmployeePreferredAreasEnabled,
    areaShifts
  ]);

  return (
    <>
      <TableWrapper>
        <Table
          data={filteredItems}
          columns={columns}
          checkboxData={checkboxData}
          showCellBorder={false}
          //critical for flickering issue with Filter
          CellRenderer={React.Fragment}
        />
      </TableWrapper>

      {projectDate && (
        <PublishCheckboxContainer>
          <CheckBox
            name="publish-to-non-booked"
            label={t('notPublishShiftsToAlreadyBookedDuringThisDay')}
            value={publishToNonBooked}
            onChange={handleChangePublishToNonBooked}
          />
        </PublishCheckboxContainer>
      )}

      {isShift && (
        <EarningsLimitCheckBox
          name="publish-to-not-exceeding-earnings-limit"
          value={earningsLimit}
          onChange={handleChangeEarningsLimit}
        />
      )}

      {showWorkedInAreaBefore && (
        <PublishCheckboxContainer>
          <CheckBox
            name="publish-to-worked-in-area-before"
            label={t('publishToWorkedInAreaBefore')}
            value={publishToWorkedInAreaBefore}
            onChange={handleChangeWorkedInAreaBefore}
          />
        </PublishCheckboxContainer>
      )}

      {showPreferredArea && (
        <PublishCheckboxContainer>
          <CheckBox
            name="publish-to-preferred-area"
            label={t('publishToPreferredArea')}
            value={publishToPreferredArea}
            onChange={handleChangePreferredArea}
          />
        </PublishCheckboxContainer>
      )}

      {clientId && (
        <PubishToPreferred>
          <CheckBox
            name="publish-to-preferred-client"
            label={t('publishToPreferredClient')}
            value={publishToPreferredClient}
            onChange={handleChangePublishToPreferredClient}
          />
        </PubishToPreferred>
      )}

      {hasTimeAvailability && (
        <PubishToPreferred>
          <CheckBox
            name="publish-to-available-employees"
            label={t('toggleCheckAvilabilityWithAvailableOnly')}
            value={availableByTimeAvailability}
            onChange={handleChangeAvailableByTimeAvailability}
          />
        </PubishToPreferred>
      )}
    </>
  );
};

export default PublishEmployees;
